// @ts-strict-ignore
import {
  email as emailRegex,
  hexColor as hexColorRegex,
  phoneNumber as phoneNumberRegex,
  rgbaColor as rgbaColorRegex,
  rgbColor as rgbColorRegex,
  urlWithoutProtocol as urlWithoutProtocolRegex,
  urlWithProtocol as urlWithProtocolRegex,
} from './regexp';

export function isURLWithProtocol(value: string): boolean {
  return urlWithProtocolRegex.test(value);
}

export function isEmail(value: string): boolean {
  // Guard against non-strings and enforce max email length per RFC 5321 (254 chars)
  // This also helps prevent potential ReDoS with extremely long inputs
  if (typeof value !== 'string' || value.length > 254) {
    return false;
  }

  return emailRegex.test(value);
}

export function isPhoneNumber(value: string): boolean {
  return phoneNumberRegex.test(value);
}

export function isValidUrl(
  url: string,
  options: { protocols: Array<'http' | 'https'> } = { protocols: ['http', 'https'] },
): boolean {
  if (typeof url !== 'string') {
    return false;
  }

  return isURLWithProtocol(url) && options.protocols.some((protocol) => url.startsWith(`${protocol}://`));
}

export function isValidUrlWithoutPrefix(url: string): boolean {
  return urlWithoutProtocolRegex.test(url);
}

export function isHexColor(color: string): boolean {
  return hexColorRegex.test(color);
}

export function isRGBA(color: string): boolean {
  return rgbaColorRegex.test(color);
}

export function isRGB(color: string): boolean {
  return rgbColorRegex.test(color);
}

export function isColor(color: string): boolean {
  return isHexColor(color) || color === 'transparent' || isRGBA(color) || isRGB(color);
}

export function validateColorField(color: string): string | undefined {
  if (!isColor(color)) {
    return 'Invalid color value.';
  }

  return undefined;
}

export function validateFileSize(file: File, maxSizeInMB = 2): string {
  if (file.size > maxSizeInMB * 1024 * 1024) {
    return `File size must be less than ${maxSizeInMB} MB.`;
  }

  return null;
}

/**
 * Validates that a path is safe for client-side navigation.
 * Prevents XSS attacks through malicious URLs (javascript:, data:, etc.)
 *
 * @param path - The path to validate
 * @returns true if the path is safe for navigation
 *
 * @example
 * isSafeNavigationPath('/settings') // true
 * isSafeNavigationPath('/archives?from=/chats') // true
 * isSafeNavigationPath('javascript:alert(1)') // false (protocol handler)
 * isSafeNavigationPath('//evil.com') // false (protocol-relative URL)
 * isSafeNavigationPath('https://evil.com') // false (doesn't start with /)
 */
export function isSafeNavigationPath(path: unknown): path is string {
  if (typeof path !== 'string') {
    return false;
  }

  // Must start with / to be a relative path
  if (!path.startsWith('/')) {
    return false;
  }

  // Block protocol handlers and path traversal that could lead to XSS
  // - // could be used to navigate to external domains (//evil.com)
  // - : could be used for protocol handlers (javascript:, data:)
  // - \ could be used for path manipulation on Windows
  if (path.includes('//') || path.includes(':') || path.includes('\\')) {
    return false;
  }

  return true;
}
